import React, { useState } from 'react';
import {
  Dialog,
  DialogContent,
  TextField,
  Typography,
  Stack,
  Button,
  FormControl,
  Select,
  MenuItem,
  Box,
  SelectChangeEvent
} from '@mui/material';
import MainCard from 'components/MainCard';
import { validateUserComment } from 'utils/userComments';

const reasons = ['Awaiting Further Customer Information', 'Awaiting Third Party Data', 'Awaiting Geodesys Response'];

interface OnHoldProps {
  open: boolean;
  onClose: () => void;
  onConfirm: (data: { reason: string; comment: string }) => Promise<boolean | void> | boolean | void;
}

const OnHold: React.FC<OnHoldProps> = ({ open, onClose, onConfirm }) => {
  const [reason, setReason] = useState<string>('Awaiting Further Customer Information');
  const [comment, setComment] = useState<string>('');

  const handleReasonChange = (event: SelectChangeEvent<string>) => {
    setReason(event.target.value);
  };

  const handleCommentChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setComment(event.target.value);
  };

  const handleConfirm = async () => {
    try {
      // Add comment against order with reason/comment
      const didConfirm = await onConfirm({ reason, comment });
      if (didConfirm !== false) {
        onClose();
      }
    } catch (error) {
      console.error('Failed to place order on hold:', error);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      sx={{
        '& .MuiDialog-paper': {
          borderRadius: 0
        },
        '& .MuiBackdrop-root': {
          backgroundColor: 'rgba(0, 0, 0, 0.7)'
        }
      }}
    >
      <DialogContent sx={{ p: 0 }}>
        <MainCard title="On Hold" sx={{ boxShadow: 'none' }}>
          <Box sx={{ p: 2 }}>
            <Typography variant="subtitle1" sx={{ mb: 2 }}>
              Reason
            </Typography>

            <FormControl fullWidth size="small" sx={{ mb: 3 }}>
              <Select
                value={reason}
                onChange={handleReasonChange}
                displayEmpty
                sx={{
                  fontSize: '0.875rem',
                  borderRadius: 0,
                  '& .MuiOutlinedInput-notchedOutline': {
                    borderColor: 'rgba(0, 0, 0, 0.23)'
                  }
                }}
              >
                {reasons.map((option) => (
                  <MenuItem key={option} value={option}>
                    {option}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <Typography variant="subtitle1" sx={{ mb: 2 }}>
              Comment
            </Typography>

            <TextField
              fullWidth
              multiline
              rows={4}
              placeholder="Enter Comment"
              value={comment}
              error={validateUserComment(comment) != null}
              helperText={validateUserComment(comment)}
              onChange={handleCommentChange}
              variant="outlined"
              size="small"
              sx={{
                mb: 3,
                fontSize: '0.875rem',
                '& .MuiOutlinedInput-root': {
                  borderRadius: 0
                }
              }}
            />

            <Stack direction="row" spacing={2} justifyContent="flex-end">
              <Button
                variant="outlined"
                onClick={onClose}
                sx={{
                  textTransform: 'none',
                  color: 'text.primary',
                  borderColor: 'rgba(0, 0, 0, 0.23)',
                  borderRadius: 0,
                  '&:hover': {
                    borderColor: 'rgba(0, 0, 0, 0.23)',
                    backgroundColor: 'rgba(0, 0, 0, 0.04)',
                    color: 'text.primary'
                  }
                }}
              >
                Back
              </Button>
              <Button
                variant="contained"
                onClick={handleConfirm}
                disabled={validateUserComment(comment) != null}
                sx={{
                  textTransform: 'none',
                  backgroundColor: '#008E9C',
                  borderRadius: 0,
                  '&:hover': {
                    backgroundColor: '#01707a'
                  }
                }}
              >
                Confirm
              </Button>
            </Stack>
          </Box>
        </MainCard>
      </DialogContent>
    </Dialog>
  );
};

export default OnHold;
